import React, { useState, useEffect } from "react";
import {
  ProcessorSpec,
  ProcessorChoice,
  useUpdateProcessorSpecMutation,
  useDeleteProcessorSpecMutation,
} from "../../api/customerFieldApi";
import { ProcessorOptionsForm } from "./ProcessorOptionsForm";
import { Dropdown } from "../../components/tailwind/dropdown/Dropdown";
import { Button } from "../../components/tailwind/button/Button";

interface Props {
  spec: ProcessorSpec;
  processorChoices: ProcessorChoice[];
}

export const ProcessorSpecItem: React.FC<Props> = ({
  spec,
  processorChoices,
}) => {
  const [updateSpec] = useUpdateProcessorSpecMutation();
  const [
    deleteSpec,
    { isLoading: isDeleting },
  ] = useDeleteProcessorSpecMutation();

  const [selectedName, setSelectedName] = useState(spec.name || "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  useEffect(() => {
    setSelectedName(spec.name || "");
  }, [spec.name]);

  const handleNameChange = (value: string): void => {
    setSelectedName(value);
    updateSpec({ id: spec.id, name: value || null });
    // Show options form when a processor is selected
    if (value) {
      setShowOptions(true);
    }
  };

  const handleOptionsChange = (options: Record<string, unknown>): void => {
    updateSpec({ id: spec.id, options });
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await deleteSpec({
        id: spec.id,
        customer_field_id: spec.customer_field_id,
      }).unwrap();
    } catch (err) {
      console.error("Failed to delete processor spec:", err);
    }
  };

  // Group processor choices for the dropdown
  const groupedChoices: Record<string, ProcessorChoice[]> = {};
  processorChoices.forEach((choice) => {
    const group = choice.group || "";
    if (!groupedChoices[group]) groupedChoices[group] = [];
    groupedChoices[group].push(choice);
  });

  // Build dropdown options with headers
  const dropdownOptions: Array<{
    key: string;
    value: string;
    text: string;
    type?: "header" | "option";
  }> = [];

  Object.entries(groupedChoices).forEach(([group, choices]) => {
    if (group) {
      dropdownOptions.push({
        key: `header-${group}`,
        value: `__header__${group}`,
        text: group,
        type: "header",
      });
    }
    choices.forEach((choice) => {
      dropdownOptions.push({
        key: choice.value,
        value: choice.value,
        text: choice.label,
      });
    });
  });

  const selectedProcessor = processorChoices.find(
    (c) => c.value === selectedName
  );

  return (
    <div className="tw-px-4 tw-py-3 tw-bg-white hover:tw-bg-gray-50 tw-transition-colors">
      <div className="tw-flex tw-items-center tw-gap-3">
        {/* Processor Type Selector */}
        <div className="tw-flex-1 tw-min-w-0">
          <Dropdown
            options={dropdownOptions}
            value={selectedName}
            onChange={(value) => handleNameChange(value as string)}
            placeholder="Select processor type..."
            clearable
          />
        </div>

        {/* Options Toggle */}
        {selectedName && (
          <button
            onClick={() => setShowOptions(!showOptions)}
            className={`tw-p-1.5 tw-rounded tw-transition-colors ${
              showOptions
                ? "tw-bg-blue-100 tw-text-blue-600"
                : "tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100"
            }`}
            title={showOptions ? "Hide options" : "Show options"}
          >
            <i className="material-icons tw-text-lg">tune</i>
          </button>
        )}

        {/* Delete Button */}
        {showDeleteConfirm ? (
          <div className="tw-flex tw-gap-1">
            <Button
              variant="primary-alt"
              size="sm"
              compact
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "..." : "Yes"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              compact
              onClick={() => setShowDeleteConfirm(false)}
            >
              No
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="tw-p-1.5 tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
            title="Delete processor"
          >
            <i className="material-icons tw-text-lg">close</i>
          </button>
        )}
      </div>

      {/* Selected Processor Info */}
      {selectedProcessor && (
        <div className="tw-mt-2 tw-text-xs tw-text-gray-500">
          {selectedProcessor.group && (
            <span className="tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-rounded tw-mr-2">
              {selectedProcessor.group}
            </span>
          )}
          <span>{selectedProcessor.label}</span>
        </div>
      )}

      {/* Options Form */}
      {showOptions && selectedName && (
        <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200">
          <ProcessorOptionsForm
            processorName={selectedName}
            currentOptions={spec.options || {}}
            onOptionsChange={handleOptionsChange}
          />
        </div>
      )}
    </div>
  );
};
